import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, BookOpen, Send, SkipForward } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { weeklyReflectionApi } from '@/services/learningManagementApi';
import { gamificationApi } from '@/services/gamificationApi';

interface ReflectionSectionProps {
  userId: string;
  learningPathId: string;
  planId: string;
  planTitle?: string;
  onComplete: () => void;
  onSkip: () => void;
}

export const ReflectionSection: React.FC<ReflectionSectionProps> = ({
  userId,
  learningPathId,
  planId,
  planTitle,
  onComplete,
  onSkip,
}) => {
  const [accomplishments, setAccomplishments] = useState('');
  const [challenges, setChallenges] = useState('');
  const [learnings, setLearnings] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = accomplishments.trim().length > 0 && rating > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const now = new Date();
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - 7);

      await weeklyReflectionApi.submit({
        userId,
        learningPathId,
        weekStartDate: weekStart.toISOString(),
        weekEndDate: now.toISOString(),
        learningPathProgressRating: rating * 2, // 1-5 stars → 2-10 scale
        skillAccomplishments: accomplishments.trim() ? [accomplishments.trim()] : [],
        skillChallenges: challenges.trim() ? [challenges.trim()] : [],
        skillLearnings: learnings.trim() ? [learnings.trim()] : [],
        metadata: { planId, planTitle },
      });

      // Record activity for gamification streak
      try { await gamificationApi.recordActivity(userId); } catch { /* non-blocking */ }

      onComplete();
    } catch (err) {
      console.error('[ReflectionSection] Submit failed:', err);
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-violet-500/5 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15">
              <BookOpen className="h-4 w-4 text-primary" />
            </div>
            Weekly Reflection
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Take a moment to reflect on your learning journey. This helps your AI coach personalize future plans.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* What went well */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">What went well this week?</label>
            <textarea
              className="w-full rounded-lg border border-border bg-background/50 px-3 py-2 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
              rows={2}
              placeholder="Skills you practiced, insights you gained..."
              value={accomplishments}
              onChange={(e) => setAccomplishments(e.target.value)}
              maxLength={500}
            />
          </div>

          {/* What was challenging */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">What was challenging?</label>
            <textarea
              className="w-full rounded-lg border border-border bg-background/50 px-3 py-2 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
              rows={2}
              placeholder="Anything you found difficult or want to improve..."
              value={challenges}
              onChange={(e) => setChallenges(e.target.value)}
              maxLength={500}
            />
          </div>

          {/* Key takeaway */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Key takeaway</label>
            <textarea
              className="w-full rounded-lg border border-border bg-background/50 px-3 py-2 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
              rows={2}
              placeholder="One thing you'll carry forward..."
              value={learnings}
              onChange={(e) => setLearnings(e.target.value)}
              maxLength={500}
            />
          </div>

          {/* Star rating */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Rate your progress</label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="p-0.5 transition-transform hover:scale-110"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  aria-label={`Rate ${star} out of 5`}
                >
                  <Star
                    className={`h-6 w-6 transition-colors ${
                      star <= (hoveredStar || rating)
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-muted-foreground/40'
                    }`}
                  />
                </button>
              ))}
              {rating > 0 && (
                <span className="ml-2 text-xs text-muted-foreground">
                  {['', 'Getting started', 'Making progress', 'Good progress', 'Great progress', 'Excellent!'][rating]}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="gap-2"
            >
              <Send className="h-4 w-4" />
              {submitting ? 'Submitting...' : 'Submit Reflection'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onSkip}
              className="text-muted-foreground"
            >
              <SkipForward className="h-3.5 w-3.5 mr-1" />
              Skip for now
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
